/**
 * Scale modes are parameter sets of one mapping (Concept §3.3). Every parameter is numeric, so the modes can be
 * interpolated for the animated switch (S1-14) and tuned live in the debug panel.
 */

export interface ScaleProfile {
  /** Exponent of the global radial mapping g(r) = AU_du · (r / AU)^α. 1 = true distances. */
  alpha: number;
  /** 0 = true body size, 1 = enlarged body size (blended in log space). */
  bodyEnlarge: number;
  /** Enlarged display radius of a Jupiter-sized body, in du. */
  enlargedRefDu: number;
  /** Enlarged radius ∝ R^β, so small bodies grow more than large ones. */
  enlargedExponent: number;
  /**
   * Moons (inside a planet's bubble) are enlarged by `bodyEnlarge · moonEnlarge` (log-space blend), so their nested
   * bubbles fit between their neighbours' (Sprint 3, D8, experiment E2).
   */
  moonEnlarge: number;
  /** 0 = logarithmic altitude mapping inside bubbles, 1 = linear (identity). */
  localLinear: number;
  /**
   * Which global scale the local mapping matches at the bubble edge: 0 = the radial scale (α·g/r), 1 = the
   * tangential scale (g/r). In between is a compromise between the two.
   */
  bubbleMatch: number;
  /** Multiplier on the logarithmic altitude scale H (live tuning). */
  hScale: number;
  /** Multiplier on the catalog bubble radii. */
  bubbleScale: number;
  /** Relative width of the transition shell at the bubble edge. */
  sigma: number;
  /** Minimum on-screen size of spacecraft sprites and body markers, px. */
  spriteMinPx: number;
}

export type ScaleMode = 'overview' | 'realistic';

/** Overview (default): everything fits on screen; flybys are zoomed logarithmically inside bubbles. */
export const OVERVIEW: Readonly<ScaleProfile> = {
  alpha: 0.4,
  bodyEnlarge: 1,
  enlargedRefDu: 1.5,
  enlargedExponent: 0.5,
  // E2 (Sprint 3): at 1 the nested bubbles overlap (Io 226,000 km vs Europa 325,000 km, 249,000 km apart) and
  // Titan's grows to 1.1·10⁶ km, nearly its whole orbit; at 0.75 they are 54k/77k/176k/290k and 254k km
  moonEnlarge: 0.75,
  localLinear: 0,
  bubbleMatch: 0.5,
  hScale: 1,
  bubbleScale: 1,
  sigma: 0.5,
  spriteMinPx: 10,
};

/** Realistic: true distances and sizes; the mapping is the identity (scaled to du). */
export const REALISTIC: Readonly<ScaleProfile> = {
  ...OVERVIEW,
  alpha: 1,
  bodyEnlarge: 0,
  localLinear: 1,
  spriteMinPx: 6,
};

export const PROFILES: Record<ScaleMode, Readonly<ScaleProfile>> = { overview: OVERVIEW, realistic: REALISTIC };

/** Component-wise linear interpolation `a → b` at `s ∈ [0, 1]` (callers apply easing). */
export function interpolateProfile(
  a: Readonly<ScaleProfile>,
  b: Readonly<ScaleProfile>,
  s: number,
  out: ScaleProfile = { ...a },
): ScaleProfile {
  for (const key of Object.keys(a) as (keyof ScaleProfile)[]) out[key] = a[key] + (b[key] - a[key]) * s;
  return out;
}

/** Smooth ease-in-out on [0, 1]. */
export function easeInOut(s: number): number {
  const x = s <= 0 ? 0 : s >= 1 ? 1 : s;
  return x * x * x * (x * (6 * x - 15) + 10);
}
